import { useState, useEffect } from 'react';
import { MessageSquare, Mail, Zap, CreditCard, TrendingUp, CheckCircle, AlertCircle, ExternalLink, Plus, Phone, Building2, Info, Loader, Clock, Shield } from 'lucide-react';
import { getMessagingStats, startMessagingSubscription, purchaseMessageBlocks, getCompanyMessagingStats, checkMessagingSubscription } from './smsUtils';
import { useTheme } from './ThemeContext';
import { useModal } from './AppModal';
import { supabaseData } from './supabaseData';

export default function MessagingSettings({ userId, companyId, userRole, colors: propColors }) {
  const { colors: themeColors } = useTheme();
  const colors = propColors || themeColors;
  const { showAlert, showSuccess, showError } = useModal();
  const [stats, setStats] = useState(null);
  const [companyStats, setCompanyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companySubscription, setCompanySubscription] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [registrationStep, setRegistrationStep] = useState('plan'); // 'plan', 'brand-info', 'review'
  const [submittingBrand, setSubmittingBrand] = useState(false);

  // Brand registration form
  const [brandForm, setBrandForm] = useState({
    company_name: '',
    entity_type: 'PRIVATE_PROFIT',
    ein: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    phone: '',
    email: '',
    website: '',
    vertical: 'HOME_SERVICES',
    description: ''
  });

  // Pricing - tiered per-user (first user + additional)
  const plans = [
    { id: 'weekly', label: 'Weekly', firstPrice: '$4/wk', additionalPrice: '$3/wk' },
    { id: 'monthly', label: 'Monthly', firstPrice: '$15/mo', additionalPrice: '$12/mo' },
  ];

  const verticals = [
    { id: 'HOME_SERVICES', label: 'Home Services' },
    { id: 'CONSTRUCTION', label: 'Construction' },
    { id: 'REAL_ESTATE', label: 'Real Estate' },
    { id: 'PROFESSIONAL_SERVICES', label: 'Professional Services' },
    { id: 'HEALTH_AND_FITNESS', label: 'Health & Fitness' },
    { id: 'AUTOMOTIVE', label: 'Automotive' },
    { id: 'AGRICULTURE', label: 'Agriculture' },
    { id: 'ENTERTAINMENT', label: 'Entertainment' },
    { id: 'RETAIL', label: 'Retail' },
    { id: 'FOOD_AND_BEVERAGE', label: 'Food & Beverage' },
    { id: 'OTHER', label: 'Other' }
  ];

  useEffect(() => {
    loadStats();
  }, [userId, companyId]);

  const loadStats = async () => {
    setLoading(true);
    try {
      // Check Work Van subscription status
      const { data: company } = await supabaseData
        .from('companies')
        .select('stripe_customer_id, subscription_status')
        .eq('id', companyId)
        .single();
      setCompanySubscription(company);

      // Load subscription status (includes brand registration info)
      const subStatus = await checkMessagingSubscription(companyId);
      const userStats = await getMessagingStats(userId, companyId);
      // Merge brand info from subscription check into stats
      setStats({ ...userStats, ...subStatus });

      if (userRole === 'owner' || userRole === 'admin') {
        const compStats = await getCompanyMessagingStats(companyId);
        setCompanyStats(compStats);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const result = await startMessagingSubscription(companyId, selectedPlan);
      if (result.direct) {
        // Subscription created directly using card on file — no redirect needed
        showSuccess('SMS subscription activated! Your card on file has been charged. Brand registration will begin shortly.');
        loadStats();
      } else if (result.url) {
        // No card on file — redirect to Stripe Checkout
        window.location.href = result.url;
      } else {
        showError(result.error || 'Failed to start subscription');
      }
    } catch (error) {
      showError('Error: ' + error.message);
    } finally {
      setSubscribing(false);
    }
  };

  const handleBuyCredits = async () => {
    try {
      const result = await purchaseMessageBlocks(companyId, 1);
      if (result.url) {
        window.location.href = result.url;
      } else {
        showError(result.error || 'Failed to purchase credits');
      }
    } catch (error) {
      showError('Error: ' + error.message);
    }
  };

  const handleBrandSubmit = async () => {
    // Validate required fields
    const required = ['company_name', 'ein', 'street', 'city', 'state', 'zip', 'phone', 'email', 'description'];
    const missing = required.filter(f => !brandForm[f]?.trim());
    if (missing.length > 0) {
      showAlert('Please fill in all required fields: ' + missing.map(f => f.replace('_', ' ')).join(', '));
      return;
    }

    // Validate EIN format
    const einClean = brandForm.ein.replace(/\D/g, '');
    if (einClean.length !== 9) {
      showAlert('EIN must be 9 digits (e.g., 12-3456789)');
      return;
    }

    setSubmittingBrand(true);
    try {
      const response = await fetch('/api/telnyx-brand', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'register-brand',
          companyId,
          brandData: {
            ...brandForm,
            ein: einClean
          }
        })
      });

      const result = await response.json();

      if (result.success) {
        showSuccess('Brand registration submitted! We\'ll register your campaign and provision your phone number. This usually takes 1-3 business days.');
        loadStats();
      } else {
        showError('Registration failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      showError('Error submitting registration: ' + error.message);
    } finally {
      setSubmittingBrand(false);
    }
  };

  const updateBrandField = (field, value) => {
    setBrandForm(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors?.textMuted || '#888' }}>
        Loading messaging settings...
      </div>
    );
  }

  const hasSubscription = stats?.hasSubscription;
  const totalCredits = stats?.credits?.total || 0;
  const usedThisPeriod = stats?.subscription?.usedThisPeriod || 0;
  const includedCredits = stats?.subscription?.included || 1000;
  const brandStatus = stats?.brandStatus || null; // 'pending', 'verified', 'failed', null
  const isTrialUser = !companySubscription?.stripe_customer_id || companySubscription?.subscription_status === 'trialing';

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    background: colors?.bgCard || '#242424',
    border: `1px solid ${colors?.border || '#333'}`,
    borderRadius: '8px',
    color: colors?.text || '#fff',
    fontSize: '0.95rem',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    color: colors?.text || '#fff',
    fontSize: '0.85rem',
    fontWeight: '500',
    marginBottom: '0.35rem'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <MessageSquare size={20} color="#fff" />
        </div>
        <div>
          <h3 style={{ margin: 0, color: colors?.text || '#fff' }}>Messaging</h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: colors?.textMuted || '#888' }}>
            SMS messaging
          </p>
        </div>
      </div>

      {/* Active Subscription - only show if actually has a paid subscription with a plan type */}
      {hasSubscription && stats?.subscription?.planType ? (
        <div style={{
          background: colors?.bgInput || '#1a1a1a',
          border: `1px solid ${colors?.border || '#333'}`,
          borderRadius: '12px',
          padding: '1.5rem'
        }}>

          {/* If showing brand registration form inline */}
          {registrationStep === 'brand-info' && !brandStatus ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Building2 size={18} color="#8b5cf6" />
                <span style={{ color: '#8b5cf6', fontWeight: '600' }}>Register Your Brand</span>
              </div>
              <p style={{ color: colors?.textMuted || '#888', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
                Complete your brand registration to activate SMS messaging. Required by carriers for 10DLC compliance.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div><label style={labelStyle}>Legal Business Name *</label><input type="text" placeholder="e.g., Josh's Garage Doors LLC" value={brandForm.company_name} onChange={(e) => updateBrandField('company_name', e.target.value)} style={inputStyle} /></div>
                <div><label style={labelStyle}>Business Entity Type *</label><select value={brandForm.entity_type} onChange={(e) => updateBrandField('entity_type', e.target.value)} style={inputStyle}>
                  <option value="SOLE_PROPRIETOR">Sole Proprietor</option>
                  <option value="PRIVATE_PROFIT">LLC / Corporation (Private)</option>
                  <option value="PUBLIC_PROFIT">Public Company</option>
                  <option value="NON_PROFIT">Non-Profit</option>
                  <option value="GOVERNMENT">Government</option>
                </select><div style={{ fontSize: '0.75rem', color: colors?.textMuted || '#888', marginTop: '0.25rem' }}>Select how your business is legally organized</div></div>
                <div><label style={labelStyle}>EIN (Tax ID) *</label><input type="text" placeholder="XX-XXXXXXX" value={brandForm.ein} onChange={(e) => { let val = e.target.value.replace(/[^\d-]/g, ''); if (val.length === 2 && !val.includes('-') && brandForm.ein.length < val.length) val = val + '-'; updateBrandField('ein', val); }} maxLength={10} style={inputStyle} /><div style={{ fontSize: '0.75rem', color: colors?.textMuted || '#888', marginTop: '0.25rem' }}>9-digit federal tax ID number</div></div>
                <div><label style={labelStyle}>Business Street Address *</label><input type="text" placeholder="123 Main St" value={brandForm.street} onChange={(e) => updateBrandField('street', e.target.value)} style={inputStyle} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
                  <div><label style={labelStyle}>City *</label><input type="text" placeholder="Phoenix" value={brandForm.city} onChange={(e) => updateBrandField('city', e.target.value)} style={inputStyle} /></div>
                  <div><label style={labelStyle}>State *</label><input type="text" placeholder="AZ" maxLength={2} value={brandForm.state} onChange={(e) => updateBrandField('state', e.target.value.toUpperCase())} style={inputStyle} /></div>
                  <div><label style={labelStyle}>ZIP *</label><input type="text" placeholder="85001" maxLength={10} value={brandForm.zip} onChange={(e) => updateBrandField('zip', e.target.value)} style={inputStyle} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div><label style={labelStyle}>Business Phone *</label><input type="tel" placeholder="(480) 555-1234" value={brandForm.phone} onChange={(e) => updateBrandField('phone', e.target.value)} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Contact Email *</label><input type="email" placeholder="you@company.com" value={brandForm.email} onChange={(e) => updateBrandField('email', e.target.value)} style={inputStyle} /></div>
                </div>
                <div><label style={labelStyle}>Website <span style={{ fontWeight: '400', color: colors?.textMuted }}>optional</span></label><input type="url" placeholder="https://yourcompany.com" value={brandForm.website} onChange={(e) => updateBrandField('website', e.target.value)} style={inputStyle} /></div>
                <div><label style={labelStyle}>Industry *</label><select value={brandForm.vertical} onChange={(e) => updateBrandField('vertical', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>{verticals.map(v => (<option key={v.id} value={v.id}>{v.label}</option>))}</select></div>
                <div><label style={labelStyle}>Business Description *</label><textarea placeholder="Brief description of your business..." value={brandForm.description} onChange={(e) => updateBrandField('description', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button onClick={() => setRegistrationStep('plan')} style={{ flex: 1, padding: '0.875rem', background: colors?.bgCard || '#242424', border: `1px solid ${colors?.border || '#333'}`, borderRadius: '8px', color: colors?.text || '#fff', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
                <button onClick={handleBrandSubmit} disabled={submittingBrand} style={{ flex: 2, padding: '0.875rem', background: submittingBrand ? colors?.border || '#333' : 'linear-gradient(135deg, #8b5cf6, #a855f7)', border: 'none', borderRadius: '8px', color: '#fff', cursor: submittingBrand ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  {submittingBrand ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Submitting...</> : 'Register Brand'}
                </button>
              </div>
            </>
          ) : (
            <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <CheckCircle size={18} color="#22c55e" />
            <span style={{ color: '#22c55e', fontWeight: '600' }}>Active Subscription</span>
            <span style={{
              marginLeft: 'auto',
              background: '#22c55e20',
              color: '#22c55e',
              padding: '0.25rem 0.75rem',
              borderRadius: '999px',
              fontSize: '0.8rem',
              fontWeight: '600',
              textTransform: 'capitalize'
            }}>
              {stats?.subscription?.planType}
            </span>
          </div>

          {/* Brand Registration Status */}
          {brandStatus === 'pending' && (
            <div style={{
              background: '#f59e0b15',
              border: '1px solid #f59e0b40',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <Clock size={18} color="#f59e0b" style={{ flexShrink: 0 }} />
              <div>
                <div style={{ color: '#f59e0b', fontWeight: '600', fontSize: '0.9rem' }}>Brand Registration Pending</div>
                <div style={{ color: colors?.textMuted || '#888', fontSize: '0.85rem' }}>
                  Your 10DLC brand is awaiting carrier approval (1-3 business days). SMS will be enabled once approved.
                </div>
              </div>
            </div>
          )}

          {brandStatus === 'verified' && (
            <div style={{
              background: '#22c55e15',
              border: '1px solid #22c55e40',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <Shield size={18} color="#22c55e" style={{ flexShrink: 0 }} />
              <div>
                <div style={{ color: '#22c55e', fontWeight: '600', fontSize: '0.9rem' }}>10DLC Verified</div>
                <div style={{ color: colors?.textMuted || '#888', fontSize: '0.85rem' }}>
                  Your brand is registered and your dedicated number is active.
                </div>
              </div>
            </div>
          )}

          {!brandStatus && (
            <div style={{
              background: '#ef444415',
              border: '1px solid #ef444440',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <AlertCircle size={18} color="#ef4444" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: '#ef4444', fontWeight: '600', fontSize: '0.9rem' }}>Brand Registration Required</div>
                <div style={{ color: colors?.textMuted || '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Register your business to activate SMS messaging. Required by carriers for 10DLC compliance.
                </div>
                <button
                  onClick={() => setRegistrationStep('brand-info')}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#ef4444',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontWeight: '600',
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                >
                  Register Now
                </button>
              </div>
            </div>
          )}

          {/* Credits Display */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: colors?.text || '#fff' }}>
                {totalCredits.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.8rem', color: colors?.textMuted || '#888' }}>
                SMS Credits Available
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: colors?.text || '#fff' }}>
                {usedThisPeriod.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.8rem', color: colors?.textMuted || '#888' }}>
                Used This Period
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: colors?.text || '#fff' }}>
                {includedCredits.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.8rem', color: colors?.textMuted || '#888' }}>
                Included/Period
              </div>
            </div>
          </div>

          {/* Credit Bar */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{
              height: '8px',
              background: colors?.border || '#333',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${Math.min(100, (usedThisPeriod / includedCredits) * 100)}%`,
                background: usedThisPeriod > includedCredits * 0.9 ? '#ef4444' : '#22c55e',
                borderRadius: '4px',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '0.25rem',
              fontSize: '0.75rem',
              color: colors?.textMuted || '#888'
            }}>
              <span>{Math.round((usedThisPeriod / includedCredits) * 100)}% of included used</span>
              <span>Resets {stats?.subscription?.periodEnd ? new Date(stats.subscription.periodEnd).toLocaleDateString() : 'N/A'}</span>
            </div>
          </div>

          {/* Buy More Credits */}
          <button
            onClick={handleBuyCredits}
            style={{
              width: '100%',
              padding: '0.875rem',
              background: colors?.bgCard || '#242424',
              border: `1px solid ${colors?.border || '#333'}`,
              borderRadius: '8px',
              color: colors?.text || '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontWeight: '500'
            }}
          >
            <Plus size={16} />
            Buy 1,000 More SMS Credits - $20
          </button>
            </>
          )}
        </div>
      ) : (
        /* No Subscription - Show Sign Up Flow */
        <div style={{
          background: colors?.bgInput || '#1a1a1a',
          border: `1px solid ${colors?.border || '#333'}`,
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          {/* Trial users must activate subscription first */}
          {isTrialUser ? (
            <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: `${colors?.warning || '#f59e0b'}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <AlertCircle size={24} color={colors?.warning || '#f59e0b'} />
              </div>
              <h3 style={{ margin: '0 0 0.5rem', color: colors?.text || '#fff' }}>
                Activate Your Subscription First
              </h3>
              <p style={{ color: colors?.textMuted || '#888', fontSize: '0.9rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                SMS messaging requires an active Work Van subscription with a payment method on file.
                Activate your plan to unlock dedicated texting with your own branded phone number.
              </p>
              <button
                onClick={() => {
                  // Navigate to subscription settings
                  window.location.hash = '#settings';
                  // Small delay to let settings load, then the subscription tab would need to be selected
                  showAlert('Go to Settings → Subscription to activate your plan.');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: colors?.accent || '#F05A28',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  cursor: 'pointer'
                }}
              >
                Activate Subscription
              </button>
            </div>
          ) : registrationStep === 'plan' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <Phone size={18} color="#8b5cf6" />
                <span style={{ color: '#8b5cf6', fontWeight: '600' }}>Get Your Own Branded Number</span>
              </div>

              <p style={{ color: colors?.textMuted || '#888', marginBottom: '1.5rem', fontSize: '0.9rem', lineHeight: '1.5' }}>
                Send SMS messages from your own dedicated phone number. Your customers see YOUR company
                as the sender. Includes 1,000 messages per billing period. Requires 10DLC brand registration
                (we handle all the paperwork).
              </p>

              {/* What's Included */}
              <div style={{
                background: colors?.bgCard || '#242424',
                borderRadius: '10px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontWeight: '600', color: colors?.text || '#fff', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                  What's Included:
                </div>
                {[
                  'Your own dedicated phone number',
                  '10DLC brand & campaign registration',
                  '1,000 SMS per user per billing period',
                  'Email sending is always free (no subscription needed)',
                  'Quick templates for common messages',
                  'Full carrier compliance handled for you'
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                    <CheckCircle size={14} color="#22c55e" style={{ flexShrink: 0 }} />
                    <span style={{ color: colors?.text || '#fff', fontSize: '0.85rem' }}>{item}</span>
                  </div>
                ))}
              </div>

              {/* Plan Selection */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                {plans.map(plan => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    style={{
                      padding: '1rem',
                      background: selectedPlan === plan.id ? '#8b5cf620' : colors?.bgCard || '#242424',
                      border: selectedPlan === plan.id ? '2px solid #8b5cf6' : `1px solid ${colors?.border || '#333'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ color: colors?.text || '#fff', fontWeight: '600', marginBottom: '0.25rem' }}>
                      {plan.label}
                    </div>
                    <div style={{ color: '#8b5cf6', fontWeight: '700', fontSize: '1.1rem' }}>
                      {plan.firstPrice}
                    </div>
                    <div style={{ color: colors?.textMuted || '#888', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      +{plan.additionalPrice} each additional user
                    </div>
                  </button>
                ))}
              </div>

              <div style={{
                textAlign: 'center',
                padding: '0.75rem',
                background: colors?.bgCard || '#242424',
                borderRadius: '6px',
                marginBottom: '1rem',
                fontSize: '0.85rem',
                color: '#f59e0b',
                border: '1px solid #f59e0b40'
              }}>
                + $20 one-time registration fee (non-refundable)
              </div>

              <button
                onClick={() => setRegistrationStep('brand-info')}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Zap size={18} />
                Continue to Registration
              </button>

              <p style={{
                color: colors?.textMuted || '#888',
                fontSize: '0.8rem',
                textAlign: 'center',
                marginTop: '0.75rem'
              }}>
                Additional SMS credits: $20 per 1,000 messages
              </p>
            </>
          )}

          {registrationStep === 'brand-info' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Building2 size={18} color="#8b5cf6" />
                <span style={{ color: '#8b5cf6', fontWeight: '600' }}>Business Registration</span>
              </div>
              <p style={{ color: colors?.textMuted || '#888', marginBottom: '1rem', fontSize: '0.85rem', lineHeight: '1.4' }}>
                Required by carriers for 10DLC SMS compliance. This info is used to register your brand
                with The Campaign Registry (TCR). Approval takes 1-3 business days.
              </p>
              <div style={{
                background: '#f59e0b15',
                border: '1px solid #f59e0b40',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                marginBottom: '1.5rem',
                fontSize: '0.85rem',
                color: '#f59e0b',
                lineHeight: '1.4'
              }}>
                <strong>Important:</strong> The $20 registration fee is non-refundable. If registration fails due to incorrect information, you will need to pay again. Please double-check all fields.
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Company Name */}
                <div>
                  <label style={labelStyle}>Legal Business Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., Josh's Garage Doors LLC"
                    value={brandForm.company_name}
                    onChange={(e) => updateBrandField('company_name', e.target.value)}
                    style={inputStyle}
                  />
                </div>

                {/* EIN */}
                <div>
                  <label style={labelStyle}>EIN (Tax ID) *</label>
                  <input
                    type="text"
                    placeholder="XX-XXXXXXX"
                    value={brandForm.ein}
                    onChange={(e) => {
                      let val = e.target.value.replace(/[^\d-]/g, '');
                      // Auto-format with dash
                      if (val.length === 2 && !val.includes('-') && brandForm.ein.length < val.length) {
                        val = val + '-';
                      }
                      updateBrandField('ein', val);
                    }}
                    maxLength={10}
                    style={inputStyle}
                  />
                  <div style={{ fontSize: '0.75rem', color: colors?.textMuted || '#888', marginTop: '0.25rem' }}>
                    9-digit federal tax ID number
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label style={labelStyle}>Business Street Address *</label>
                  <input
                    type="text"
                    placeholder="123 Main St"
                    value={brandForm.street}
                    onChange={(e) => updateBrandField('street', e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={labelStyle}>City *</label>
                    <input
                      type="text"
                      placeholder="Phoenix"
                      value={brandForm.city}
                      onChange={(e) => updateBrandField('city', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>State *</label>
                    <input
                      type="text"
                      placeholder="AZ"
                      maxLength={2}
                      value={brandForm.state}
                      onChange={(e) => updateBrandField('state', e.target.value.toUpperCase())}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>ZIP *</label>
                    <input
                      type="text"
                      placeholder="85001"
                      maxLength={10}
                      value={brandForm.zip}
                      onChange={(e) => updateBrandField('zip', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Phone & Email */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={labelStyle}>Business Phone *</label>
                    <input
                      type="tel"
                      placeholder="(480) 555-1234"
                      value={brandForm.phone}
                      onChange={(e) => updateBrandField('phone', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Contact Email *</label>
                    <input
                      type="email"
                      placeholder="you@company.com"
                      value={brandForm.email}
                      onChange={(e) => updateBrandField('email', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Website (optional) */}
                <div>
                  <label style={labelStyle}>Website <span style={{ color: colors?.textMuted || '#888', fontWeight: '400' }}>(optional)</span></label>
                  <input
                    type="url"
                    placeholder="https://yourcompany.com"
                    value={brandForm.website}
                    onChange={(e) => updateBrandField('website', e.target.value)}
                    style={inputStyle}
                  />
                </div>

                {/* Industry */}
                <div>
                  <label style={labelStyle}>Industry *</label>
                  <select
                    value={brandForm.vertical}
                    onChange={(e) => updateBrandField('vertical', e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    {verticals.map(v => (
                      <option key={v.id} value={v.id}>{v.label}</option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label style={labelStyle}>Business Description *</label>
                  <textarea
                    placeholder="Brief description of your business and what services you provide to customers..."
                    value={brandForm.description}
                    onChange={(e) => updateBrandField('description', e.target.value)}
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                  <div style={{ fontSize: '0.75rem', color: colors?.textMuted || '#888', marginTop: '0.25rem' }}>
                    This is shared with carriers for brand verification
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  onClick={() => setRegistrationStep('plan')}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    background: colors?.bgCard || '#242424',
                    border: `1px solid ${colors?.border || '#333'}`,
                    borderRadius: '8px',
                    color: colors?.text || '#fff',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '0.95rem'
                  }}
                >
                  Back
                </button>
                <button
                  onClick={() => setRegistrationStep('review')}
                  style={{
                    flex: 2,
                    padding: '0.875rem',
                    background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '0.95rem'
                  }}
                >
                  Review & Subscribe
                </button>
              </div>
            </>
          )}

          {registrationStep === 'review' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <CheckCircle size={18} color="#22c55e" />
                <span style={{ color: '#22c55e', fontWeight: '600' }}>Review & Confirm</span>
              </div>

              {/* Plan Summary */}
              <div style={{
                background: colors?.bgCard || '#242424',
                borderRadius: '10px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ fontWeight: '600', color: colors?.text || '#fff', marginBottom: '0.75rem' }}>Plan</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.9rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: colors?.text || '#fff' }}>1st user</span>
                    <span style={{ color: '#8b5cf6', fontWeight: '700' }}>{plans.find(p => p.id === selectedPlan)?.firstPrice || ''}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: colors?.text || '#fff' }}>Additional users</span>
                    <span style={{ color: '#8b5cf6', fontWeight: '700' }}>{plans.find(p => p.id === selectedPlan)?.additionalPrice || ''} each</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${colors?.border || '#333'}`, paddingTop: '0.35rem', marginTop: '0.25rem' }}>
                    <span style={{ color: '#f59e0b' }}>Registration fee (non-refundable)</span>
                    <span style={{ color: '#f59e0b', fontWeight: '700' }}>$20</span>
                  </div>
                </div>
              </div>

              {/* Brand Info Summary */}
              <div style={{
                background: colors?.bgCard || '#242424',
                borderRadius: '10px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ fontWeight: '600', color: colors?.text || '#fff', marginBottom: '0.75rem' }}>Business Info</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.85rem' }}>
                  <div><span style={{ color: colors?.textMuted || '#888' }}>Name:</span> <span style={{ color: colors?.text || '#fff' }}>{brandForm.company_name}</span></div>
                  <div><span style={{ color: colors?.textMuted || '#888' }}>EIN:</span> <span style={{ color: colors?.text || '#fff' }}>{brandForm.ein}</span></div>
                  <div><span style={{ color: colors?.textMuted || '#888' }}>Phone:</span> <span style={{ color: colors?.text || '#fff' }}>{brandForm.phone}</span></div>
                  <div><span style={{ color: colors?.textMuted || '#888' }}>Email:</span> <span style={{ color: colors?.text || '#fff' }}>{brandForm.email}</span></div>
                  <div style={{ gridColumn: 'span 2' }}><span style={{ color: colors?.textMuted || '#888' }}>Address:</span> <span style={{ color: colors?.text || '#fff' }}>{brandForm.street}, {brandForm.city}, {brandForm.state} {brandForm.zip}</span></div>
                </div>
                <button
                  onClick={() => setRegistrationStep('brand-info')}
                  style={{
                    marginTop: '0.75rem',
                    padding: '0.35rem 0.75rem',
                    background: 'none',
                    border: `1px solid ${colors?.border || '#333'}`,
                    borderRadius: '4px',
                    color: colors?.textMuted || '#888',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  Edit
                </button>
              </div>

              {/* What Happens Next */}
              <div style={{
                background: '#8b5cf610',
                border: '1px solid #8b5cf630',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem',
                fontSize: '0.85rem',
                color: colors?.text || '#fff',
                lineHeight: '1.5'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>What happens next:</div>
                1. You'll be redirected to complete payment<br />
                2. We register your brand with carriers (1-3 days)<br />
                3. Your dedicated number is provisioned automatically<br />
                4. You can start sending messages
              </div>

              {/* Subscribe Button */}
              <button
                onClick={async () => {
                  // Save brand data first, then redirect to Stripe
                  setSubmittingBrand(true);
                  try {
                    // Store brand registration data
                    const brandResp = await fetch('/api/telnyx-brand', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        action: 'save-brand-data',
                        companyId,
                        brandData: {
                          ...brandForm,
                          ein: brandForm.ein.replace(/\D/g, '')
                        }
                      })
                    });
                    const brandResult = await brandResp.json();
                    if (!brandResult.success) {
                      showError('Failed to save registration data: ' + (brandResult.error || 'Unknown error'));
                      return;
                    }

                    // Now redirect to Stripe checkout
                    await handleSubscribe();
                  } catch (error) {
                    showError('Error: ' + error.message);
                  } finally {
                    setSubmittingBrand(false);
                  }
                }}
                disabled={subscribing || submittingBrand}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: (subscribing || submittingBrand) ? colors?.border || '#333' : 'linear-gradient(135deg, #8b5cf6, #a855f7)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: (subscribing || submittingBrand) ? 'not-allowed' : 'pointer',
                  fontWeight: '700',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {(subscribing || submittingBrand) ? (
                  <><Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Processing...</>
                ) : (
                  <><Zap size={18} /> Subscribe & Register</>
                )}
              </button>
            </>
          )}
          }
        </div>
      )}

      {/* Brand registration form is now shown inline within the subscription card */}

      {/* This Month's Usage */}
      {hasSubscription && stats?.thisMonth && (
        <div style={{
          background: colors?.bgInput || '#1a1a1a',
          border: `1px solid ${colors?.border || '#333'}`,
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: colors?.text || '#fff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <TrendingUp size={16} />
            This Month
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
            <div style={{ background: colors?.bgCard || '#242424', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <MessageSquare size={20} color="#6366f1" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: colors?.text || '#fff' }}>{stats.thisMonth.sms}</div>
              <div style={{ fontSize: '0.8rem', color: colors?.textMuted || '#888' }}>SMS Sent</div>
            </div>
            <div style={{ background: colors?.bgCard || '#242424', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <Mail size={20} color="#22c55e" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: colors?.text || '#fff' }}>{stats.thisMonth.email}</div>
              <div style={{ fontSize: '0.8rem', color: colors?.textMuted || '#888' }}>Emails Sent</div>
            </div>
            <div style={{ background: colors?.bgCard || '#242424', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <Zap size={20} color="#f59e0b" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: colors?.text || '#fff' }}>{stats.thisMonth.total}</div>
              <div style={{ fontSize: '0.8rem', color: colors?.textMuted || '#888' }}>Total</div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Team Overview */}
      {(userRole === 'owner' || userRole === 'admin') && companyStats && (
        <div style={{
          background: colors?.bgInput || '#1a1a1a',
          border: `1px solid ${colors?.border || '#333'}`,
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: colors?.text || '#fff' }}>Team Messaging Overview</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors?.text || '#fff' }}>{companyStats.userCount || 0}</div><div style={{ fontSize: '0.8rem', color: colors?.textMuted || '#888' }}>Team Members</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors?.text || '#fff' }}>{companyStats.thisMonth?.sms || 0}</div><div style={{ fontSize: '0.8rem', color: colors?.textMuted || '#888' }}>SMS This Month</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors?.text || '#fff' }}>{companyStats.thisMonth?.email || 0}</div><div style={{ fontSize: '0.8rem', color: colors?.textMuted || '#888' }}>Emails This Month</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors?.text || '#fff' }}>{companyStats.credits?.total || 0}</div><div style={{ fontSize: '0.8rem', color: colors?.textMuted || '#888' }}>SMS Credits</div></div>
          </div>

          {companyStats.users?.length > 0 && (
            <div style={{ background: colors?.bgCard || '#242424', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem', borderBottom: `1px solid ${colors?.border || '#333'}`, color: colors?.textMuted || '#888', fontSize: '0.85rem', fontWeight: '500' }}>
                Usage by Team Member (This Month)
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors?.border || '#333'}` }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: colors?.textMuted || '#888', fontWeight: '500', fontSize: '0.8rem' }}>User</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', color: colors?.textMuted || '#888', fontWeight: '500', fontSize: '0.8rem' }}>SMS</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', color: colors?.textMuted || '#888', fontWeight: '500', fontSize: '0.8rem' }}>Email</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', color: colors?.textMuted || '#888', fontWeight: '500', fontSize: '0.8rem' }}>SMS Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {companyStats.users.map((user, idx) => (
                    <tr key={idx} style={{ borderBottom: idx < companyStats.users.length - 1 ? `1px solid ${colors?.border || '#333'}` : 'none' }}>
                      <td style={{ padding: '0.75rem', color: colors?.text || '#fff', fontSize: '0.9rem' }}>{user.name}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', color: colors?.text || '#fff', fontSize: '0.9rem' }}>{user.thisMonth?.sms || 0}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', color: colors?.text || '#fff', fontSize: '0.9rem' }}>{user.thisMonth?.email || 0}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', color: '#f59e0b', fontSize: '0.9rem', fontWeight: '600' }}>{user.thisMonth?.credits || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
